import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Save,
  Trash2,
  Wrench,
  DollarSign,
  ShieldCheck,
  Paperclip,
  History,
  Building2,
  Warehouse,
  Loader2,
  X,
  Check,
  AlertTriangle,
  ChevronDown,
  FileText,
  ShoppingCart,
  Package,
  Truck,
} from 'lucide-react';
import productService from '../services/productService';
import Layout from '../components/Layout';
import ExpandableSection from '../components/ExpandableSection';
import SmartField from '../components/SmartField';
import FileAttachmentManager from '../components/FileAttachmentManager';
import InventoryHistoryComponent from '../components/InventoryHistoryComponent';
import ClientInventoryManager from '../components/ClientInventoryManager';
import ClientAttributeOverride from '../components/ClientAttributeOverride';
import ClientSelector from '../components/ClientSelector';

// Debounce utility function
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  return debouncedCallback;
};

// Helper function to format date for display
const formatLinkedDocDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    // Handle MongoDB date format
    const dateStr = dateValue.$date || dateValue;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return '';
  }
};

// Collapsible section for linked documents
const LinkedDocumentSection = ({ icon, title, count, documents, labelField, labelPrefix = '', showType = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full p-2 rounded-md hover:bg-background/50 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-medium text-sm">{title}</span>
            <span className="text-xs text-muted-foreground bg-background/80 px-1.5 py-0.5 rounded">
              {count} 个
            </span>
          </div>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              isOpen && "transform rotate-180"
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 ml-6 space-y-1 max-h-48 overflow-y-auto">
          {documents.map((doc, idx) => (
            <div
              key={doc.id || idx}
              className="flex items-center justify-between py-1.5 px-2 text-xs bg-background/50 rounded border border-border/50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground truncate">{doc.number}</span>
                  {showType && doc.type && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {doc.type === 'inbound' ? '入库' : doc.type === 'outbound' ? '出库' : doc.type}
                    </span>
                  )}
                </div>
                {doc[labelField] && (
                  <div className="text-muted-foreground truncate">
                    {labelPrefix}{doc[labelField]}
                  </div>
                )}
              </div>
              {doc.date && (
                <span className="text-muted-foreground ml-2 flex-shrink-0">
                  {formatLinkedDocDate(doc.date)}
                </span>
              )}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ProductFormPage = ({ viewMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get('referrer') || 'products';
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdProductId, setCreatedProductId] = useState(null);
  const [duplicateProductIdError, setDuplicateProductIdError] = useState(false);
  const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState('');

  // Track original product_id for change detection and cascade update dialog
  const [originalProductId, setOriginalProductId] = useState('');
  const [productIdChangeDialogOpen, setProductIdChangeDialogOpen] = useState(false);
  const [linkedDocuments, setLinkedDocuments] = useState(null);
  const [linkedDocsLoading, setLinkedDocsLoading] = useState(false);

  const categoryInputRef = useRef(null);

  // Client selection state
  const [selectedClientId, setSelectedClientId] = useState('global');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientData, setClientData] = useState({});
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    product_id: '',
    part_number: '',
    client_ref_number: '',
    category: '',
    specification: '',
    status: 'active',
    remark: '',

    // Manufacturing Parameters
    material: '硬质合金',
    coating: '无涂层',
    tip_diameter: '',
    total_length: '',
    shank_diameter: '',
    tolerance: '',
    degree: '',
    blade_type: '',
    guide_pillar_length: '',
    guide_pillar_tolerance: '',
    marking: '',

    // Pricing & Inventory
    price: '',
    unit: '支',
    min_order_qty: '',
    lead_time: '',
    stock_status: '有库存',

    // Quality & Compliance
    certification: '',
    quality_grade: '',
    inspection_standard: ''
  });

  // Validation functions using useRef to prevent infinite re-renders
  const duplicateErrorRef = useRef(duplicateProductIdError);

  useEffect(() => {
    duplicateErrorRef.current = duplicateProductIdError;
  }, [duplicateProductIdError]);

  const validateProductId = useCallback((value) => {
    if (!value) return "产品编号不能为空";
    if (duplicateErrorRef.current) return duplicateErrorMessage || "该产品编号和规格组合已存在";
    return true;
  }, [duplicateErrorMessage]);

  const validateDimension = useCallback((value) => {
    if (value && isNaN(parseFloat(value))) return "请输入有效数字";
    return true;
  }, []);

  const validatePriceField = useCallback((value) => {
    if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
      return "请输入有效的价格";
    }
    return true;
  }, []);

  const validateMinOrder = useCallback((value) => {
    if (value && (isNaN(parseInt(value)) || parseInt(value) < 1)) {
      return "最小起订量不能少于1";
    }
    return true;
  }, []);

  const handleCategorySearchInternal = async (inputValue) => {
    if (!inputValue || inputValue.length < 1) {
      setCategoryOptions([]);
      return;
    }

    setCategoryLoading(true);
    try {
      const response = await productService.searchCategories(inputValue);
      setCategoryOptions(response.categories || []);
    } catch (err) {
      console.error('Failed to search categories:', err);
      setCategoryOptions([]);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCategorySearch = useDebounce(handleCategorySearchInternal, 300);

  const handleProductIdCheck = async (productId, specification, clientRefNumber) => {
    if (!productId) {
      setDuplicateProductIdError(false);
      setDuplicateErrorMessage('');
      return;
    }

    if (isEditMode &&
        formData.product_id === productId &&
        formData.specification === specification &&
        formData.client_ref_number === clientRefNumber) {
      setDuplicateProductIdError(false);
      setDuplicateErrorMessage('');
      return;
    }

    setDuplicateCheckLoading(true);
    try {
      const result = await productService.validateProductId(
        productId,
        specification || formData.specification,
        clientRefNumber || formData.client_ref_number,
        isEditMode ? id : null
      );
      if (!result.isValid) {
        setDuplicateProductIdError(true);
        setDuplicateErrorMessage(result.message || '该产品组合已存在');
        setSuccess('');
      } else {
        setDuplicateProductIdError(false);
        setDuplicateErrorMessage('');
      }
    } catch (err) {
      console.error('Product validation check failed:', err);
      setDuplicateProductIdError(false);
      setDuplicateErrorMessage('');
    } finally {
      setDuplicateCheckLoading(false);
    }
  };

  const debouncedProductIdCheck = useDebounce(handleProductIdCheck, 500);

  const handleSmartFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [fieldName]: value
      };

      if (fieldName === 'specification' && prev.product_id) {
        debouncedProductIdCheck(prev.product_id, value, prev.client_ref_number);
      } else if (fieldName === 'client_ref_number' && prev.product_id) {
        debouncedProductIdCheck(prev.product_id, prev.specification, value);
      }

      return updated;
    });

    if ((fieldName === 'product_id' || fieldName === 'specification' || fieldName === 'client_ref_number') && duplicateErrorRef.current) {
      setDuplicateProductIdError(false);
      setDuplicateErrorMessage('');
      setError('');
    }
  }, [debouncedProductIdCheck]);

  const handleClientChange = useCallback((clientId, client) => {
    setSelectedClientId(clientId);
    setSelectedClient(client);

    if (selectedClientId !== 'global' && clientId !== selectedClientId) {
      setClientData(prev => ({
        ...prev,
        [selectedClientId]: {
          formOverrides: {},
          inventoryData: {}
        }
      }));
    }
  }, [selectedClientId]);

  const handleCategoryChange = useCallback((value) => {
    handleSmartFieldChange('category', value);
    handleCategorySearch(value);
    setShowCategorySuggestions(true);
  }, [handleSmartFieldChange, handleCategorySearch]);

  const handleCategorySelect = useCallback((category) => {
    handleSmartFieldChange('category', category);
    setShowCategorySuggestions(false);
  }, [handleSmartFieldChange]);

  useEffect(() => {
    if (isEditMode && id) {
      const loadProduct = async () => {
        setLoadingProduct(true);
        try {
          const product = await productService.getProduct(id);
          setFormData({
            name: product.name || '',
            product_id: product.product_id || '',
            part_number: product.part_number || '',
            client_ref_number: product.client_ref_number || '',
            category: product.category || '',
            specification: product.specification || '',
            status: product.status || 'active',
            remark: product.remark || '',
            material: product.material || '',
            coating: product.coating || '',
            tip_diameter: product.tip_diameter || '',
            total_length: product.total_length || '',
            shank_diameter: product.shank_diameter || '',
            tolerance: product.tolerance || '',
            degree: product.degree || '',
            blade_type: product.blade_type || '',
            guide_pillar_length: product.guide_pillar_length || '',
            guide_pillar_tolerance: product.guide_pillar_tolerance || '',
            marking: product.marking || '',
            price: product.price || '',
            unit: product.unit || '支',
            min_order_qty: product.min_order_qty || '',
            lead_time: product.lead_time || '',
            stock_status: product.stock_status || '有库存',
            certification: product.certification || '',
            quality_grade: product.quality_grade || '',
            inspection_standard: product.inspection_standard || ''
          });
          // Store original product_id for change detection
          setOriginalProductId(product.product_id || '');
        } catch (err) {
          setError(err.response?.data?.error || '加载产品失败');
        } finally {
          setLoadingProduct(false);
        }
      };
      loadProduct();
    }
  }, [isEditMode, id]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryInputRef.current && !categoryInputRef.current.contains(e.target)) {
        setShowCategorySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (duplicateProductIdError) {
      setError('请先解决产品编号和规格组合重复的问题后再提交');
      return;
    }

    // In edit mode, check if product_id is being changed and show confirmation dialog
    if (isEditMode && formData.product_id !== originalProductId && originalProductId) {
      // Open dialog and fetch linked documents count
      setProductIdChangeDialogOpen(true);
      setLinkedDocsLoading(true);
      try {
        const linked = await productService.getLinkedDocuments(id);
        // Store full response including linked_documents and linked_counts
        setLinkedDocuments(linked);
      } catch (err) {
        console.error('Failed to fetch linked documents:', err);
        setLinkedDocuments({
          linked_documents: { sales_orders: [], quotations: [], purchase_orders: [], warehouse_movements: [] },
          linked_counts: { sales_orders: 0, quotations: 0, purchase_orders: 0, warehouse_movements: 0, total: 0 }
        });
      } finally {
        setLinkedDocsLoading(false);
      }
      return; // Don't submit yet, wait for confirmation
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...formData,
        tip_diameter: formData.tip_diameter ? parseFloat(formData.tip_diameter) : undefined,
        total_length: formData.total_length ? parseFloat(formData.total_length) : undefined,
        degree: formData.degree ? parseFloat(formData.degree) : undefined,
        guide_pillar_length: formData.guide_pillar_length ? parseFloat(formData.guide_pillar_length) : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        min_order_qty: formData.min_order_qty ? parseInt(formData.min_order_qty) : undefined,
      };

      if (selectedClientId !== 'global' && selectedClient) {
        submitData.client_context = {
          client_id: selectedClientId,
          client_name: selectedClient.name,
          configured_for_client: true,
          client_data: clientData[selectedClientId] || {}
        };
      }

      Object.keys(submitData).forEach(key => {
        if (!submitData[key] && submitData[key] !== 0) {
          delete submitData[key];
        }
      });

      if (isEditMode) {
        await productService.updateProduct(id, submitData);
        setSuccess('产品更新成功！');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const response = await productService.createProduct(submitData);

        let newProductObjectId = null;

        const extractObjectId = (obj) => {
          if (!obj) return null;
          if (obj._id) {
            if (typeof obj._id === 'string') return obj._id;
            if (obj._id.$oid) return obj._id.$oid;
            if (typeof obj._id === 'object') return String(obj._id);
          }
          if (obj.id && typeof obj.id === 'string') return obj.id;
          return null;
        };

        newProductObjectId = extractObjectId(response);
        if (!newProductObjectId && response.data) {
          newProductObjectId = extractObjectId(response.data);
        }
        if (!newProductObjectId && response.product) {
          newProductObjectId = extractObjectId(response.product);
        }

        if (!newProductObjectId) {
          setError('产品创建成功，但无法获取产品ObjectId。请刷新页面查看。');
          return;
        }

        setCreatedProductId(newProductObjectId);
        setSuccessModalOpen(true);
        setSuccess('');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || `${isEditMode ? '更新' : '创建'}产品失败`;

      if (err.response?.status === 409 &&
          (errorMessage.toLowerCase().includes('product with this id') ||
           errorMessage.toLowerCase().includes('combination already exists'))) {
        setDuplicateProductIdError(true);

        const parts = [`产品编号 "${formData.product_id}"`];
        if (formData.specification) {
          parts.push(`规格 "${formData.specification}"`);
        }
        if (formData.client_ref_number) {
          parts.push(`物料编码 "${formData.client_ref_number}"`);
        }

        const combinationMsg = parts.join(' + ') + ' 的组合已存在';
        setDuplicateErrorMessage(combinationMsg);
        setError(`产品组合重复: ${combinationMsg}`);
      } else {
        setDuplicateProductIdError(false);
        setDuplicateErrorMessage('');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await productService.deleteProduct(id);
      setSuccess('Product deleted successfully!');
      setTimeout(() => {
        switch (referrer) {
          case 'sales':
          case 'quotations':
            navigate(-1);
            break;
          case 'products':
          default:
            navigate('/products');
            break;
        }
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || '删除产品失败');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  // Handler for confirmed product_id change (after user approves in dialog)
  const handleConfirmedProductIdChange = async () => {
    setProductIdChangeDialogOpen(false);
    setLinkedDocuments(null);

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...formData,
        tip_diameter: formData.tip_diameter ? parseFloat(formData.tip_diameter) : undefined,
        total_length: formData.total_length ? parseFloat(formData.total_length) : undefined,
        degree: formData.degree ? parseFloat(formData.degree) : undefined,
        guide_pillar_length: formData.guide_pillar_length ? parseFloat(formData.guide_pillar_length) : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        min_order_qty: formData.min_order_qty ? parseInt(formData.min_order_qty) : undefined,
      };

      if (selectedClientId !== 'global' && selectedClient) {
        submitData.client_context = {
          client_id: selectedClientId,
          client_name: selectedClient.name,
          configured_for_client: true,
          client_data: clientData[selectedClientId] || {}
        };
      }

      Object.keys(submitData).forEach(key => {
        if (!submitData[key] && submitData[key] !== 0) {
          delete submitData[key];
        }
      });

      const response = await productService.updateProduct(id, submitData);

      // Check if cascade update was performed
      if (response.cascade_update) {
        const { sales_orders_updated, quotations_updated, purchase_orders_updated } = response.cascade_update;
        const totalUpdated = sales_orders_updated + quotations_updated + purchase_orders_updated;
        if (totalUpdated > 0) {
          setSuccess(`产品更新成功！已同步更新 ${sales_orders_updated} 个销售订单、${quotations_updated} 个报价单、${purchase_orders_updated} 个采购订单中的产品编号。`);
        } else {
          setSuccess('产品更新成功！');
        }
      } else {
        setSuccess('产品更新成功！');
      }

      // Update original product_id after successful save
      setOriginalProductId(formData.product_id);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      const errorMessage = err.response?.data?.error || '更新产品失败';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    switch (referrer) {
      case 'sales':
        navigate(-1);
        break;
      case 'quotations':
        navigate(-1);
        break;
      case 'products':
      default:
        navigate('/products');
        break;
    }
  };

  const handleCreateAnother = () => {
    setSuccessModalOpen(false);
    const currentCategory = formData.category;
    const currentClientId = selectedClientId;
    const currentClient = selectedClient;

    setFormData({
      name: '',
      product_id: '',
      part_number: '',
      client_ref_number: '',
      category: currentCategory,
      specification: '',
      status: 'active',
      remark: '',
      material: '硬质合金',
      coating: '无涂层',
      tip_diameter: '',
      total_length: '',
      shank_diameter: '',
      tolerance: '',
      degree: '',
      blade_type: '',
      guide_pillar_length: '',
      guide_pillar_tolerance: '',
      marking: '',
      price: '',
      unit: '支',
      min_order_qty: '',
      lead_time: '',
      stock_status: '有库存',
      certification: '',
      quality_grade: '',
      inspection_standard: ''
    });

    setSelectedClientId(currentClientId);
    setSelectedClient(currentClient);
    setCreatedProductId(null);
    setError('');
    setSuccess('');
    setDuplicateProductIdError(false);
    setDuplicateErrorMessage('');
    setDuplicateCheckLoading(false);
  };

  const handleStayInEditMode = () => {
    setSuccessModalOpen(false);
    const referrerParam = referrer !== 'products' ? `?referrer=${referrer}` : '';
    navigate(`/products/${createdProductId}/edit${referrerParam}`, { replace: true });
  };

  // eslint-disable-next-line no-unused-vars
  const handleCloseSuccessModal = useCallback(() => {
    setSuccessModalOpen(false);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleClearError = useCallback(() => {
    setError('');
    setDuplicateProductIdError(false);
    setDuplicateErrorMessage('');
  }, []);

  const handleOpenDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  if (loadingProduct) {
    return (
      <Layout title={isEditMode ? "编辑产品" : "创建新产品"} activeTab="products">
        <div className="max-w-3xl mx-auto mt-8 mb-8 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEditMode ? "编辑产品" : "创建新产品"} activeTab="products">
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Header */}
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {referrer === 'sales' ? '返回销售页面' :
             referrer === 'quotations' ? '返回报价页面' :
             '返回产品列表'}
          </Button>
          <h1 className="text-2xl font-bold text-foreground">
            {isEditMode ? "编辑产品" : "创建新产品"}
          </h1>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleClearError}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-4 border-success/50 bg-success/10">
            <AlertDescription className="text-success">{success}</AlertDescription>
          </Alert>
        )}

        <Card className="border bg-card rounded-lg overflow-hidden">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <form onSubmit={handleSubmit}>
              {/* Client Context Selection */}
              <ExpandableSection
                title="客户上下文"
                icon={Building2}
                defaultExpanded={true}
                helpText="选择特定客户以配置客户专属的产品属性和库存信息"
                className="mb-6 border-l-4 border-l-primary"
              >
                <div className="mb-4">
                  <ClientSelector
                    selectedClientId={selectedClientId}
                    onClientChange={handleClientChange}
                    showGlobalOption={true}
                    label="选择客户视图"
                    placeholder="选择全局视图或特定客户"
                    helperText={
                      selectedClientId === 'global'
                        ? "全局视图 - 管理通用产品信息"
                        : selectedClient?.name
                          ? `客户视图 - ${selectedClient.name} - 可配置专属属性和库存`
                          : "客户视图 - 可配置专属属性和库存"
                    }
                    size="medium"
                  />
                </div>

                {selectedClientId !== 'global' && selectedClient && (
                  <Alert className="mt-4 border-info/50 bg-info/10">
                    <Building2 className="h-4 w-4 text-info" />
                    <AlertDescription className="text-info">
                      当前为 <strong>{selectedClient.name}</strong> 配置专属产品信息。
                      在此模式下，您可以设置客户专属的产品属性、定价和库存管理。
                    </AlertDescription>
                  </Alert>
                )}
              </ExpandableSection>

              {/* Basic Information Section */}
              <ExpandableSection
                title="基本信息"
                defaultExpanded={true}
                required={true}
                helpText="产品的基础标识和分类信息"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <SmartField
                      name="name"
                      label="产品名称"
                      value={formData.name}
                      onChange={handleSmartFieldChange}
                      required
                      placeholder="请输入产品名称"
                      helpText="产品的完整名称"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <SmartField
                      name="status"
                      label="状态"
                      type="select"
                      value={formData.status}
                      onChange={handleSmartFieldChange}
                      options={['active', 'inactive']}
                      helpText="产品状态"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="product_id"
                      label="产品编号"
                      value={formData.product_id}
                      onChange={handleSmartFieldChange}
                      onBlur={(value) => {
                        if (value) {
                          debouncedProductIdCheck(value, formData.specification, formData.client_ref_number);
                        }
                      }}
                      required
                      placeholder="请输入唯一产品编号"
                      helpText={
                        duplicateCheckLoading
                          ? "检查产品组合中..."
                          : duplicateProductIdError
                            ? duplicateErrorMessage || "该产品组合已存在"
                            : "产品编号 + 规格 + 物料编码组合必须唯一"
                      }
                      validation={validateProductId}
                      error={duplicateProductIdError}
                      loading={duplicateCheckLoading}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="part_number"
                      label="零件号"
                      value={formData.part_number}
                      onChange={handleSmartFieldChange}
                      placeholder="请输入零件号（可选）"
                      helpText="制造商零件号或型号"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="client_ref_number"
                      label="物料编码"
                      value={formData.client_ref_number}
                      onChange={handleSmartFieldChange}
                      onBlur={(value) => {
                        if (formData.product_id) {
                          debouncedProductIdCheck(formData.product_id, formData.specification, value);
                        }
                      }}
                      placeholder="请输入物料编码（可选）"
                      helpText="客户或内部物料编码"
                    />
                  </div>
                  <div className="md:col-span-12">
                    <div className="space-y-2" ref={categoryInputRef}>
                      <Label className="text-sm font-medium">
                        产品分类 <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          value={formData.category}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          onFocus={() => setShowCategorySuggestions(categoryOptions.length > 0)}
                          placeholder="请选择或输入产品分类"
                          required
                        />
                        {categoryLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        {showCategorySuggestions && categoryOptions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
                            {categoryOptions.map((option, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleCategorySelect(option)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">产品所属的业务分类</p>
                    </div>
                  </div>
                  <div className="md:col-span-12">
                    <SmartField
                      name="specification"
                      label="技术规格"
                      value={formData.specification}
                      onChange={handleSmartFieldChange}
                      onBlur={(value) => {
                        if (formData.product_id) {
                          debouncedProductIdCheck(formData.product_id, value, formData.client_ref_number);
                        }
                      }}
                      multiline
                      rows={4}
                      placeholder="请输入详细的技术规格、特性和要求..."
                      helpText="产品的详细技术参数说明"
                    />
                  </div>
                  <div className="md:col-span-12">
                    <SmartField
                      name="remark"
                      label="备注说明"
                      value={formData.remark}
                      onChange={handleSmartFieldChange}
                      multiline
                      rows={3}
                      placeholder="附加说明、注意事项或特殊要求..."
                      helpText="其他重要信息或备注"
                    />
                  </div>
                </div>
              </ExpandableSection>

              {/* Manufacturing Parameters Section */}
              <ExpandableSection
                title="制造参数"
                icon={Wrench}
                badge={[formData.material, formData.coating, formData.tip_diameter, formData.total_length, formData.shank_diameter, formData.tolerance, formData.degree, formData.blade_type, formData.guide_pillar_length, formData.guide_pillar_tolerance, formData.marking].filter(Boolean).length}
                defaultExpanded={false}
                helpText="产品的物理和材料规格参数"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-6">
                    <SmartField
                      name="material"
                      label="材料"
                      type="autocomplete"
                      value={formData.material}
                      onChange={handleSmartFieldChange}
                      category={formData.category}
                      helpText="产品主要材料类型"
                      autoSuggest
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="coating"
                      label="涂层"
                      type="autocomplete"
                      value={formData.coating}
                      onChange={handleSmartFieldChange}
                      category={formData.category}
                      helpText="表面处理或涂层类型"
                      autoSuggest
                    />
                  </div>
                  <div className="md:col-span-4">
                    <SmartField
                      name="tip_diameter"
                      label="刃径"
                      type="number"
                      value={formData.tip_diameter}
                      onChange={handleSmartFieldChange}
                      unit="mm"
                      placeholder="0.0"
                      helpText="产品刃径尺寸"
                      validation={validateDimension}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <SmartField
                      name="total_length"
                      label="总长度"
                      type="number"
                      value={formData.total_length}
                      onChange={handleSmartFieldChange}
                      unit="mm"
                      placeholder="0.0"
                      helpText="产品总长度尺寸"
                      validation={validateDimension}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <SmartField
                      name="shank_diameter"
                      label="柄径"
                      type="number"
                      value={formData.shank_diameter}
                      onChange={handleSmartFieldChange}
                      unit="mm"
                      placeholder="0.0"
                      helpText="刀柄直径尺寸"
                      validation={validateDimension}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="tolerance"
                      label="公差等级"
                      value={formData.tolerance}
                      onChange={handleSmartFieldChange}
                      placeholder="例如: ±0.01mm 或 IT7"
                      helpText="尺寸精度要求"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="degree"
                      label="角度"
                      type="number"
                      value={formData.degree}
                      onChange={handleSmartFieldChange}
                      unit="°"
                      placeholder="0.0"
                      helpText="产品角度参数"
                      validation={validateDimension}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="blade_type"
                      label="刃型"
                      value={formData.blade_type}
                      onChange={handleSmartFieldChange}
                      placeholder="请输入刃型（可选）"
                      helpText="产品的刃型规格"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="guide_pillar_length"
                      label="导柱长度"
                      type="number"
                      value={formData.guide_pillar_length}
                      onChange={handleSmartFieldChange}
                      unit="mm"
                      placeholder="0.0"
                      helpText="导柱长度尺寸"
                      validation={validateDimension}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="guide_pillar_tolerance"
                      label="导柱直径公差"
                      value={formData.guide_pillar_tolerance}
                      onChange={handleSmartFieldChange}
                      placeholder="例如: ±0.005mm"
                      helpText="导柱直径的公差要求"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="marking"
                      label="打标"
                      value={formData.marking}
                      onChange={handleSmartFieldChange}
                      placeholder="请输入打标信息（可选）"
                      helpText="产品打标或标识信息"
                    />
                  </div>
                </div>
              </ExpandableSection>

              {/* Pricing & Inventory Section */}
              <ExpandableSection
                title="价格与库存"
                icon={DollarSign}
                badge={[formData.price, formData.unit, formData.min_order_qty, formData.lead_time].filter(Boolean).length}
                defaultExpanded={false}
                helpText="产品的定价和库存管理信息"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-6">
                    <SmartField
                      name="price"
                      label="参考价格"
                      type="number"
                      value={formData.price}
                      onChange={handleSmartFieldChange}
                      unit="¥"
                      placeholder="0.00"
                      helpText="产品参考售价"
                      validation={validatePriceField}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="unit"
                      label="单位"
                      type="autocomplete"
                      value={formData.unit}
                      onChange={handleSmartFieldChange}
                      category={formData.category}
                      helpText="计量单位"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="min_order_qty"
                      label="最小起订量"
                      type="number"
                      value={formData.min_order_qty}
                      onChange={handleSmartFieldChange}
                      placeholder="1"
                      helpText="最小订购数量"
                      validation={validateMinOrder}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="lead_time"
                      label="交货周期"
                      value={formData.lead_time}
                      onChange={handleSmartFieldChange}
                      placeholder="例如: 现货、3-5工作日、2周"
                      helpText="从订单到交货的时间"
                    />
                  </div>
                  <div className="md:col-span-12">
                    <SmartField
                      name="stock_status"
                      label="库存状态"
                      type="select"
                      value={formData.stock_status}
                      onChange={handleSmartFieldChange}
                      options={['有库存', '库存不足', '无库存', '按订单生产']}
                      helpText="当前库存情况"
                    />
                  </div>
                </div>
              </ExpandableSection>

              {/* Quality & Compliance Section */}
              <ExpandableSection
                title="质量与认证"
                icon={ShieldCheck}
                badge={[formData.certification, formData.quality_grade, formData.inspection_standard].filter(Boolean).length}
                defaultExpanded={false}
                helpText="产品质量标准和认证信息"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-6">
                    <SmartField
                      name="certification"
                      label="认证标准"
                      value={formData.certification}
                      onChange={handleSmartFieldChange}
                      placeholder="例如: ISO 9001, CE, RoHS"
                      helpText="产品获得的认证或标准"
                    />
                  </div>
                  <div className="md:col-span-6">
                    <SmartField
                      name="quality_grade"
                      label="质量等级"
                      type="select"
                      value={formData.quality_grade}
                      onChange={handleSmartFieldChange}
                      options={['标准级', '精密级', '超精密级', '工业级', '航空级']}
                      helpText="产品质量分级"
                    />
                  </div>
                  <div className="md:col-span-12">
                    <SmartField
                      name="inspection_standard"
                      label="检验标准"
                      value={formData.inspection_standard}
                      onChange={handleSmartFieldChange}
                      placeholder="质量检验标准或测试方法"
                      helpText="产品质量检验的标准或程序"
                    />
                  </div>
                </div>
              </ExpandableSection>

              {/* File Attachments Section */}
              <ExpandableSection
                title="产品附件"
                icon={Paperclip}
                defaultExpanded={false}
                helpText="与产品相关的文件和文档"
              >
                {!isEditMode && !createdProductId ? (
                  <div className="p-6 text-center bg-muted/50 rounded-lg border border-dashed border-muted-foreground/30">
                    <Paperclip className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-sm font-medium mb-1">
                      附件功能将在产品创建后可用
                    </p>
                    <p className="text-sm text-muted-foreground">
                      请先保存产品基本信息，然后即可上传相关文档和图片
                    </p>
                  </div>
                ) : (
                  <FileAttachmentManager
                    entityType="products"
                    entityId={id || createdProductId}
                    title="产品文档与附件"
                    maxFiles={20}
                    maxSizeMB={100}
                    allowedTypes={[
                      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
                      '.jpg', '.jpeg', '.png', '.gif', '.bmp',
                      '.dwg', '.dxf', '.step', '.stp', '.iges', '.igs',
                      '.zip', '.rar', '.7z'
                    ]}
                    compact={false}
                  />
                )}
              </ExpandableSection>

              {/* Inventory History Section */}
              {isEditMode && id && (
                <ExpandableSection
                  title="库存历史记录"
                  icon={History}
                  defaultExpanded={false}
                  helpText="查看产品的库存变化历史记录"
                >
                  <InventoryHistoryComponent
                    productId={id}
                    productInfo={{
                      product_id: formData.product_id,
                      name: formData.name,
                      _id: id
                    }}
                    selectedClientId={selectedClientId}
                    compact={false}
                    maxHeight={500}
                  />
                </ExpandableSection>
              )}

              {/* Client-Specific Attributes Section */}
              {(selectedClientId !== 'global' && selectedClient) && (
                <ExpandableSection
                  title="客户专属配置"
                  icon={Building2}
                  badge={selectedClient?.name ? 1 : 0}
                  defaultExpanded={selectedClientId !== 'global'}
                  helpText={`管理 ${selectedClient?.name || '客户'} 的专属产品属性、定价和规格覆盖`}
                  className="border-l-4 border-l-secondary"
                >
                  {!isEditMode && !id ? (
                    <div className="p-6 text-center bg-warning/10 rounded-lg border border-dashed border-warning/30">
                      <Building2 className="h-12 w-12 mx-auto mb-4 text-warning/50" />
                      <p className="text-sm font-medium mb-1">
                        客户专属配置将在产品创建后可用
                      </p>
                      <p className="text-sm text-muted-foreground">
                        请先保存产品基本信息，然后即可配置 {selectedClient?.name} 的专属属性和定价
                      </p>
                    </div>
                  ) : (
                    <ClientAttributeOverride
                      productId={id || createdProductId || 'new'}
                      productInfo={formData}
                      clientId={selectedClientId}
                      clientInfo={selectedClient}
                      onOverrideUpdate={() => {
                        console.log('Client overrides updated for:', selectedClient?.name);
                      }}
                      readOnly={false}
                    />
                  )}
                </ExpandableSection>
              )}

              {/* Client Inventory Management Section */}
              {(selectedClientId !== 'global' && selectedClient) && (
                <ExpandableSection
                  title="客户库存管理"
                  icon={Warehouse}
                  badge={selectedClient?.name ? 1 : 0}
                  defaultExpanded={false}
                  helpText={`管理产品在 ${selectedClient?.name || '客户'} 处的库存数量和存储位置`}
                  className="border-l-4 border-l-success"
                >
                  {!isEditMode && !id ? (
                    <div className="p-6 text-center bg-info/10 rounded-lg border border-dashed border-info/30">
                      <Warehouse className="h-12 w-12 mx-auto mb-4 text-info/50" />
                      <p className="text-sm font-medium mb-1">
                        客户库存管理将在产品创建后可用
                      </p>
                      <p className="text-sm text-muted-foreground">
                        请先保存产品基本信息，然后即可管理在 {selectedClient?.name} 处的库存
                      </p>
                    </div>
                  ) : (
                    <ClientInventoryManager
                      productId={id || createdProductId}
                      productInfo={formData}
                      clientId={selectedClientId}
                      clientInfo={selectedClient}
                      onInventoryUpdate={() => {
                        console.log('Client inventory updated for:', selectedClient?.name);
                      }}
                      readOnly={false}
                    />
                  )}
                </ExpandableSection>
              )}

              {/* Form Actions */}
              <div className={cn(
                "flex gap-4 pt-4 border-t mt-4",
                isEditMode ? "justify-between" : "justify-end"
              )}>
                {isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleOpenDeleteDialog}
                    disabled={loading}
                    className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    删除
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    取消
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {isEditMode ? '更新中...' : '创建中...'}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isEditMode ? '更新产品' : '创建产品'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>确认删除</DialogTitle>
              <DialogDescription>
                您确定要删除此产品吗？此操作不可撤销。
                <br />
                <strong>产品: {formData.name} ({formData.product_id})</strong>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseDeleteDialog}
                disabled={loading}
              >
                取消
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    删除中...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    删除
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Product ID Change Confirmation Dialog */}
        <Dialog open={productIdChangeDialogOpen} onOpenChange={setProductIdChangeDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                确认更改产品编号
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-4 pt-4">
                  <p>
                    您正在将产品编号从 <strong className="text-foreground">"{originalProductId}"</strong> 更改为{' '}
                    <strong className="text-foreground">"{formData.product_id}"</strong>
                  </p>

                  {/* Impact Summary */}
                  {linkedDocsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span>正在检查关联文档...</span>
                    </div>
                  ) : linkedDocuments && linkedDocuments.linked_counts?.total > 0 ? (
                    <Alert className="border-warning/50 bg-warning/10">
                      <AlertDescription>
                        <p className="font-medium mb-3">此产品关联了以下文档：</p>
                        <div className="space-y-2">
                          {/* Sales Orders */}
                          {linkedDocuments.linked_counts?.sales_orders > 0 && (
                            <LinkedDocumentSection
                              icon={<ShoppingCart className="h-4 w-4" />}
                              title="销售订单"
                              count={linkedDocuments.linked_counts.sales_orders}
                              documents={linkedDocuments.linked_documents?.sales_orders || []}
                              labelField="client"
                            />
                          )}
                          {/* Quotations */}
                          {linkedDocuments.linked_counts?.quotations > 0 && (
                            <LinkedDocumentSection
                              icon={<FileText className="h-4 w-4" />}
                              title="报价单"
                              count={linkedDocuments.linked_counts.quotations}
                              documents={linkedDocuments.linked_documents?.quotations || []}
                              labelField="client"
                            />
                          )}
                          {/* Purchase Orders */}
                          {linkedDocuments.linked_counts?.purchase_orders > 0 && (
                            <LinkedDocumentSection
                              icon={<Package className="h-4 w-4" />}
                              title="采购订单"
                              count={linkedDocuments.linked_counts.purchase_orders}
                              documents={linkedDocuments.linked_documents?.purchase_orders || []}
                              labelField="client"
                              labelPrefix="供应商: "
                            />
                          )}
                          {/* Warehouse Movements */}
                          {linkedDocuments.linked_counts?.warehouse_movements > 0 && (
                            <LinkedDocumentSection
                              icon={<Truck className="h-4 w-4" />}
                              title="仓库移动记录"
                              count={linkedDocuments.linked_counts.warehouse_movements}
                              documents={linkedDocuments.linked_documents?.warehouse_movements || []}
                              labelField="client"
                              showType={true}
                            />
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert className="border-muted bg-muted/50">
                      <AlertDescription>此产品暂无关联的订单或报价单。</AlertDescription>
                    </Alert>
                  )}

                  {/* Explanation */}
                  <Alert className="border-info/50 bg-info/10">
                    <AlertDescription className="text-sm">
                      <strong>重要说明：</strong>确认后，产品编号将在以下位置同步更新：
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>产品目录（主记录）</li>
                        <li>所有关联的销售订单</li>
                        <li>所有关联的报价单</li>
                        <li>所有关联的采购订单</li>
                        <li>所有关联的仓库移动记录</li>
                      </ul>
                      <p className="mt-2 text-muted-foreground">
                        关联基于系统内部ID（_id），更改产品编号不会断开关联关系。
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setProductIdChangeDialogOpen(false);
                  setLinkedDocuments(null);
                }}
              >
                取消
              </Button>
              <Button onClick={handleConfirmedProductIdChange} disabled={linkedDocsLoading}>
                确认更改
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Success Modal */}
        <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader className="text-center">
              <div className="flex flex-col items-center mb-4">
                <div className="p-3 rounded-full bg-success/10 mb-4">
                  <Check className="h-12 w-12 text-success" />
                </div>
                <DialogTitle className="text-xl font-semibold">
                  产品创建成功！
                </DialogTitle>
              </div>
              <DialogDescription className="text-center">
                <p className="mb-4">
                  产品 <strong>"{formData.name}"</strong> ({formData.product_id}) 已成功创建。
                </p>

                {selectedClientId !== 'global' && selectedClient && (
                  <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-primary/20 text-left">
                    <p className="text-sm text-primary font-medium mb-1 flex items-center">
                      <Building2 className="h-4 w-4 mr-2" />
                      客户专属配置已启用
                    </p>
                    <p className="text-sm text-muted-foreground">
                      此产品已为 <strong>{selectedClient.name}</strong> 启用专属配置。
                      您可以在编辑模式下设置客户专属的属性、定价和库存管理。
                    </p>
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  您现在可以为此产品上传附件、配置客户专属设置，或者创建另一个类似的产品。
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2 justify-center">
              <Button variant="outline" onClick={handleCancel}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {referrer === 'sales' ? '返回销售页面' :
                 referrer === 'quotations' ? '返回报价页面' :
                 '返回产品列表'}
              </Button>
              <Button variant="outline" onClick={handleCreateAnother}>
                创建类似产品
              </Button>
              <Button onClick={handleStayInEditMode}>
                继续编辑
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProductFormPage;
